import { Button, CloseButton, FileButton, Group, Text } from "@mantine/core";
import { AudioLines, CloudUpload, Library, Mic, Upload } from "lucide-react";
import { useEffect, useRef, useState, type DragEvent } from "react";

import type { TalkingHeadCaps } from "../../../../api/generated/model";
import { ReferencesDialog } from "../../../../platform/media/ReferencesDialog";
import { useImageUpload } from "../../../../platform/media/image-upload";
import { AudioRecorder } from "../../../../platform/voices/AudioRecorder";
import { VoicePreview } from "../../../../platform/voices/VoicePreview";
import { readDuration } from "../../../../platform/voices/audio-file";
import {
  ENTRY_TITLE,
  RECORD_DESCRIPTION,
  RECORD_TITLE,
  SPEECH_TITLE,
  speechHint,
  speechProblems,
  type SpeechAudio,
} from "./speech";
import classes from "./talking-head.module.css";

/** A file taken, before it is saved: its own address to play, and what is wrong with it. */
interface Taken {
  file: File;
  url: string;
  problems: string[];
}

export interface SpeechSectionProps {
  teamId: string;
  brandId: string;
  /** The model's talking-head block: the formats and length its audio takes. */
  caps: TalkingHeadCaps | null;
  speech: SpeechAudio | null;
  onSpeech: (speech: SpeechAudio | null) => void;
  disabled: boolean;
}

/**
 * Live's "Speech" (`speech-to-speech-section`, LEFTOVERS 9): one audio every actor speaks.
 * Without one, live's entry: the drop zone, "Upload audio or record your voice", the model's
 * rule, and "Upload Audio", "From Library" (live's "Audio References") and "Start Recording".
 * A recording or a file shows in live's preview with its problems, a name and Save, which puts
 * it in the library; the saved audio is a tile with live's x.
 */
export function SpeechSection({ teamId, brandId, caps, speech, onSpeech, disabled }: SpeechSectionProps) {
  const [recording, setRecording] = useState(false);
  const [taken, setTaken] = useState<Taken | null>(null);
  const [library, setLibrary] = useState(false);
  const running = useRef<AbortController | null>(null);
  const hint = speechHint(caps);
  const drop = () =>
    setTaken((current) => {
      if (current !== null) URL.revokeObjectURL(current.url);
      return null;
    });
  const upload = useImageUpload({
    teamId,
    brandId,
    kind: "audio",
    onUploaded: (asset) => {
      drop();
      onSpeech({ assetId: asset.id, name: asset.name, durationMs: asset.duration_ms });
    },
    onUploading: (controller) => {
      running.current = controller;
    },
  });
  // A file read for its length, then shown, problems and all (live shows an invalid one too).
  const take = async (file: File | null, recorded = false) => {
    if (file === null) return;
    setRecording(false);
    const seconds = await readDuration(file);
    const url = URL.createObjectURL(file);
    setTaken((current) => {
      if (current !== null) URL.revokeObjectURL(current.url);
      return { file, url, problems: speechProblems(file, seconds, caps, recorded) };
    });
  };
  const takenUrl = useRef<string | null>(null);
  useEffect(() => {
    takenUrl.current = taken?.url ?? null;
  });
  useEffect(
    () => () => {
      running.current?.abort();
      if (takenUrl.current !== null) URL.revokeObjectURL(takenUrl.current);
    },
    [],
  );
  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (disabled) return;
    void take(event.dataTransfer.files[0] ?? null);
  };
  let body;
  if (speech !== null) {
    body = (
      <div className={classes.speechTile}>
        <AudioLines size={20} strokeWidth={1.5} aria-hidden />
        <span className={classes.voiceName} title={speech.name}>
          {speech.name}
        </span>
        <CloseButton
          size="sm"
          disabled={disabled}
          aria-label={`Remove ${speech.name}`}
          onClick={() => onSpeech(null)}
        />
      </div>
    );
  } else if (taken !== null) {
    body = (
      <VoicePreview
        url={taken.url}
        label={taken.file.name}
        problems={taken.problems}
        onReset={drop}
        // The name the file is saved under, as live renames it.
        onSave={(name) => void upload.send(new File([taken.file], name, { type: taken.file.type }))}
        saving={upload.pending}
        progress={upload.progress}
        failure={upload.problem}
      />
    );
  } else if (recording) {
    body = (
      <AudioRecorder
        title={RECORD_TITLE}
        description={RECORD_DESCRIPTION}
        hint={hint}
        disabled={disabled}
        onRecorded={(file) => void take(file, true)}
      />
    );
  } else {
    body = (
      // The zone takes a dropped file, as live's; its buttons are the keyboard's way.
      // oxlint-disable-next-line jsx-a11y/no-static-element-interactions
      <div
        className={classes.speechEntry}
        data-disabled={disabled || undefined}
        onDragOver={(event) => event.preventDefault()}
        onDrop={onDrop}
      >
        <CloudUpload size={40} strokeWidth={1.25} aria-hidden />
        <Text className={classes.speechEntryTitle}>{ENTRY_TITLE}</Text>
        {hint === "" ? null : <Text className={classes.speechHint}>{hint}</Text>}
        <Group gap={16} justify="center">
          <FileButton
            accept={(caps?.audio_mimes ?? ["audio/*"]).join(",")}
            disabled={disabled}
            onChange={(file) => void take(file)}
            inputProps={{ "aria-label": "Choose an audio to upload" }}
          >
            {(buttonProps) => (
              <Button {...buttonProps} variant="outline" leftSection={<Upload size={16} />}>
                Upload Audio
              </Button>
            )}
          </FileButton>
          <Button
            variant="outline"
            disabled={disabled}
            leftSection={<Library size={16} />}
            onClick={() => setLibrary(true)}
          >
            From Library
          </Button>
          <Button disabled={disabled} leftSection={<Mic size={16} />} onClick={() => setRecording(true)}>
            Start Recording
          </Button>
        </Group>
      </div>
    );
  }
  return (
    <div className={classes.speech}>
      <Text className={classes.scriptLabel}>{SPEECH_TITLE}</Text>
      {body}
      {library ? (
        <ReferencesDialog
          teamId={teamId}
          brandId={brandId}
          kind="audio"
          title="Audio References"
          max={1}
          chosen={[]}
          onClose={() => setLibrary(false)}
          onConfirm={(picked) => {
            const audio = picked[0];
            if (audio !== undefined) {
              drop();
              onSpeech({ assetId: audio.assetId, name: audio.name, durationMs: audio.durationMs ?? null });
            }
          }}
        />
      ) : null}
    </div>
  );
}
